import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useLang } from "@/App";
import Nav, { THERAPIST_PHOTO } from "@/components/Nav";
import LegalFooter from "@/components/LegalFooter";
import CookieBanner from "@/components/CookieBanner";
import ExploreColorPicker from "@/components/ExploreColorPicker";
import VideoModal from "@/components/VideoModal";
import Seo from "@/components/Seo";
import { VIDEOS, FEATURED_TESTIMONIALS, FEATURED_VIDEOS, bookingWhatsAppUrl } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const openBooking = (lang, service = "") => window.open(bookingWhatsAppUrl(lang, service), "_blank", "noopener,noreferrer");

/* ---------- Hero ---------- */
function Hero() {
  const { t, lang } = useLang();
  return (
    <section id="hero" className="pt-32 pb-20 md:pt-40 md:pb-28">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="eyebrow mb-6" data-testid="hero-eyebrow">{t.hero.eyebrow}</div>
          <h1 className="h-display" style={{ fontSize: "clamp(48px, 7vw, 96px)" }}>
            <span data-testid="hero-title-1">{t.hero.title1}</span>
            <br />
            <span className="h-italic" data-testid="hero-title-2">{t.hero.title2}</span>
          </h1>
          <p className="mt-8 max-w-xl" style={{ fontSize: 18, lineHeight: 1.6, color: "var(--ink-soft)" }} data-testid="hero-subtitle">
            {t.hero.sub}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <div className="relative">
              <button
                className="btn-pill btn-dark"
                onClick={() => openBooking(lang)}
                data-testid="hero-cta-book"
              >
                {t.hero.cta1}
              </button>
              <span
                className="absolute -top-3 -right-3 sm:-right-5 rounded-full whitespace-nowrap hero-badge-pop"
                style={{ background: "#c9a961", color: "#0e0e0e", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, padding: "5px 10px", boxShadow: "0 8px 20px -8px rgba(201,169,97,0.8)", transform: "rotate(-4deg)" }}
                data-testid="hero-mini-badge"
              >
                {t.hero.badge}
              </span>
            </div>
            <button
              className="btn-pill btn-outline"
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-cta-discover"
            >
              {t.hero.cta2}
            </button>
          </div>
        </div>

        <div className="lg:col-span-5 fade-up delay-2">
          <div className="relative">
            <div
              className="absolute -inset-4 rounded-[36px]"
              style={{ background: "linear-gradient(135deg, #f4ecd8 0%, #e8dcc0 100%)", zIndex: 0 }}
            />
            <div className="relative rounded-[28px] overflow-hidden" style={{ aspectRatio: "4/5", boxShadow: "0 30px 80px -30px rgba(60,50,20,0.3)" }}>
              <img
                src={THERAPIST_PHOTO}
                alt={lang === "ro" ? "Silvana Capris, psiholog clinician — ședințe online" : "Silvana Capris, clinical psychologist — online sessions"}
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 20%" }}
                data-testid="hero-therapist-photo"
              />
            </div>
            <div
              className="absolute -bottom-5 -left-5 px-5 py-3 rounded-full"
              style={{ background: "#c9a961", color: "#0e0e0e", fontSize: 12, letterSpacing: "0.16em", fontWeight: 600, boxShadow: "0 12px 30px -12px rgba(201,169,97,0.9)" }}
              data-testid="hero-badge"
            >
              SILVANA CAPRIS
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Quote ---------- */
function Quote() {
  const { t } = useLang();
  return (
    <section className="relative grain" style={{ background: "linear-gradient(180deg,#f4ecd8 0%,#ede2c6 100%)", padding: "36px 0" }} data-testid="quote-section">
      <div className="container-x text-center fade-up">
        <div className="eyebrow mb-3" data-testid="quote-eyebrow">{t.quote.eyebrow}</div>
        <blockquote className="serif italic mx-auto" style={{ fontSize: "clamp(18px, 2.2vw, 24px)", maxWidth: 760, color: "var(--ink)", lineHeight: 1.4 }} data-testid="quote-text">
          <span className="serif" style={{ color: "var(--olive-deep)", marginRight: 6 }}>“</span>{t.quote.text}<span className="serif" style={{ color: "var(--olive-deep)", marginLeft: 6 }}>”</span>
        </blockquote>
      </div>
    </section>
  );
}

/* ---------- Services ---------- */
function Services() {
  const { t } = useLang();
  return (
    <section id="services" className="section">
      <div className="container-x">
        <div className="eyebrow mb-3" data-testid="services-eyebrow">{t.services.eyebrow}</div>
        <h2 className="h-display mb-16 md:whitespace-nowrap" style={{ fontSize: "clamp(30px, 3.6vw, 50px)" }} data-testid="services-title">
          {t.services.title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
          {t.services.list.map((s, i) => (
            <div key={i} className="fade-up" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`service-${i}`}>
              <div style={{ width: 36, height: 1, background: "var(--gold)", marginBottom: 20 }} />
              <h3 className="serif" style={{ fontSize: 26, marginBottom: 12, color: "var(--ink)" }}>{s.t}</h3>
              <p style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.65 }}>{s.d}</p>
              {s.href && (
                <a href={s.href} className="inline-flex items-center gap-1 mt-3 nav-link" data-testid={`service-link-${i}`} style={{ color: "var(--olive-deep)", fontSize: 13, letterSpacing: "0.04em", fontWeight: 600 }}>
                  {t.services.learnMore} <span aria-hidden="true">→</span>
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- About ---------- */
function AboutVideoModal({ about, lang, miniCta, onClose }) {
  return (
    <VideoModal
      testId="about-video-modal"
      title={`${about.videoLabel} · ${about.caption}`}
      src={about.video}
      webm={about.videoWebm}
      captions={about.captions}
      lang={lang}
      labels={{ close: about.close, ccLabel: about.ccLabel, ccOff: about.ccOff, tapSound: about.tapSound }}
      onClose={onClose}
    >
      <div className="mt-4 flex justify-center">
        <button
          className="btn-pill text-xs"
          style={{ background: "#c9a961", color: "#0e0e0e", padding: "12px 24px" }}
          onClick={() => { onClose(); openBooking(lang); }}
          data-testid="about-video-book-mini"
        >
          {miniCta}
        </button>
      </div>
    </VideoModal>
  );
}

function About() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [hoverArmed, setHoverArmed] = useState(true);
  const close = React.useCallback(() => { setOpen(false); setHoverArmed(false); }, []);
  const a = t.about;
  return (
    <section id="about" className="section" style={{ background: "#f7f1e1" }}>
      <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5 md:sticky md:top-28">
          <div
            className="relative rounded-[24px] overflow-hidden flex flex-col items-center justify-center text-center cursor-pointer group"
            style={{ aspectRatio: "4/5", background: "linear-gradient(160deg,#0e0e0e 0%,#2d3a2a 100%)", border: "1px solid #c9a961" }}
            onMouseEnter={() => a.video && hoverArmed && setOpen(true)}
            onMouseLeave={() => setHoverArmed(true)}
            onClick={() => a.video && setOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && a.video) setOpen(true); }}
            aria-label={a.videoLabel}
            data-testid="about-video-holder"
          >
            <img src={THERAPIST_PHOTO} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 20%", opacity: 0.28, filter: "grayscale(30%)" }} />
            <span className="relative inline-flex items-center justify-center rounded-full transition-transform group-hover:scale-110" style={{ width: 84, height: 84, background: "#c9a961", boxShadow: "0 20px 50px -15px rgba(201,169,97,0.7)" }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="#0e0e0e"><path d="M8 5v14l11-7z" /></svg>
            </span>
            <div className="relative mt-6 text-xs uppercase tracking-widest" style={{ color: "#fbf8f1" }}>{a.videoLabel}</div>
            <div className="relative mt-2 text-xs" style={{ color: "#e2dcc6", opacity: 0.85 }} data-testid="about-video-hint">{a.videoHint}</div>
          </div>
          <p className="mt-4 text-center serif italic" style={{ color: "var(--olive-deep)" }} data-testid="about-caption">{a.caption}</p>
        </div>
        <div className="md:col-span-7">
          <div className="eyebrow mb-3" data-testid="about-eyebrow">{a.eyebrow}</div>
          <h2 className="h-display mb-8" style={{ fontSize: "clamp(34px, 4.5vw, 56px)" }} data-testid="about-title">{a.title}</h2>
          <div className="space-y-5" style={{ fontSize: 17, lineHeight: 1.7, color: "var(--ink-soft)", maxWidth: 620 }} data-testid="about-body">
            {a.body.map((p, i) => <p key={i}>{p}</p>)}
          </div>

          <div className="mt-14 pt-10" style={{ borderTop: "1px solid var(--line)" }} data-testid="about-education">
            <div style={{ width: 36, height: 1, background: "var(--gold)", marginBottom: 20 }} />
            <h3 className="serif mb-8" style={{ fontSize: 30, color: "var(--ink)" }} data-testid="about-education-title">{a.education.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
              {a.education.groups.map((g, gi) => (
                <div key={gi} data-testid={`about-education-group-${gi}`}>
                  <div className="text-xs uppercase tracking-widest mb-4" style={{ color: "var(--olive-deep)" }}>{g.heading}</div>
                  <ul className="space-y-2.5">
                    {g.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-3" style={{ color: "var(--ink-soft)", fontSize: 14.5, lineHeight: 1.55 }} data-testid={`about-education-${gi}-item-${i}`}>
                        <span className="mt-1.5 flex-shrink-0 rounded-full" style={{ width: 5, height: 5, background: "var(--gold)" }} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {open && <AboutVideoModal about={a} lang={lang} miniCta={t.book.miniCta} onClose={close} />}
    </section>
  );
}

/* ---------- Conversations / YouTube ---------- */
function Conversations() {
  const { t } = useLang();
  const [active, setActive] = useState(null); // active video id
  // Lock body scroll when modal open + close on Escape
  useEffect(() => {
    if (active) {
      document.body.style.overflow = "hidden";
      const onKey = (e) => { if (e.key === "Escape") setActive(null); };
      window.addEventListener("keydown", onKey);
      return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
    }
  }, [active]);

  return (
    <section id="conversations" className="section">
      <div className="container-x">
        <div className="eyebrow mb-3" data-testid="convo-eyebrow">{t.conversations.eyebrow}</div>
        <h2 className="h-display mb-3" style={{ fontSize: "clamp(32px, 4vw, 48px)" }} data-testid="convo-title">{t.conversations.title}</h2>
        <p className="mb-12" style={{ color: "var(--ink-soft)", maxWidth: 600 }} data-testid="convo-sub">{t.conversations.sub}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {VIDEOS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setActive(v.id)}
              className="group text-left"
              data-testid={`video-${v.id}`}
            >
              <div className="rounded-2xl overflow-hidden mb-3 relative" style={{ aspectRatio: "16/10" }}>
                <img src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`} alt={`${v.kind} — ${v.ep} · Silvana Capris, psiholog`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(251,248,241,0.92)" }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="#0e0e0e"><path d="M3 1l9 6-9 6V1z" /></svg>
                  </div>
                </div>
              </div>
              <div className="text-xs uppercase tracking-widest" style={{ color: "var(--olive-deep)" }}>{v.kind}</div>
              <div className="serif mt-1" style={{ fontSize: 18 }}>{v.ep}</div>
            </button>
          ))}
        </div>

        <div className="mt-16 pt-10" style={{ borderTop: "1px solid var(--line)" }} data-testid="radio-partnership">
          <div className="flex items-center gap-4 mb-7">
            <img src="/brand/radio-romania.webp" alt="Radio România Actualități" style={{ height: 38, width: "auto" }} data-testid="radio-logo" />
            <div>
              <div className="serif" style={{ fontSize: 22, lineHeight: 1.1 }}>{t.conversations.radio.heading}</div>
              <div className="text-xs uppercase tracking-widest mt-1" style={{ color: "var(--olive-deep)" }}>{t.conversations.radio.partner}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {t.conversations.radio.episodes.map((ep, i) => (
              <div key={i} className="rounded-2xl p-5" style={{ background: "#f7f1e1", border: "1px solid var(--line)" }} data-testid={`radio-episode-${i}`}>
                <div className="serif mb-3" style={{ fontSize: 16, lineHeight: 1.35 }}>{ep.title}</div>
                <audio controls preload="none" src={ep.src} style={{ width: "100%" }} data-testid={`radio-audio-${i}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Video modal */}
      {active && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: "rgba(10,10,8,0.85)", backdropFilter: "blur(6px)" }}
          onClick={() => setActive(null)}
          role="dialog"
          aria-modal="true"
          data-testid="video-modal"
        >
          <div
            className="relative w-full"
            style={{ maxWidth: 1100 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActive(null)}
              aria-label="Close video"
              data-testid="video-modal-close"
              className="absolute -top-12 right-0 text-sm flex items-center gap-2"
              style={{ color: "#fbf8f1" }}
            >
              <span className="hidden sm:inline text-xs uppercase tracking-widest">Close</span>
              <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(251,248,241,0.12)" }}>✕</span>
            </button>
            <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: "16/9", background: "#000" }}>
              <iframe
                title={`YouTube video ${active}`}
                src={`https://www.youtube-nocookie.com/embed/${active}?autoplay=1&rel=0&modestbranding=1`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: "100%", height: "100%", border: 0 }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- Explore flip cards ---------- */
function CardVideoSlot({ card, label, watchLabel, onWatch }) {
  if (card.video) {
    return (
      <button
        type="button"
        className="w-full rounded-lg mb-3 flex items-center justify-center gap-2 transition-colors hover:brightness-95"
        style={{ height: 84, background: "rgba(45,58,42,0.12)", border: "1px solid #c9a961", cursor: "pointer" }}
        onClick={(e) => { e.stopPropagation(); onWatch(card); }}
        data-testid={`card-video-watch-${card.n}`}
      >
        <span
          className="inline-flex items-center justify-center rounded-full"
          style={{ width: 36, height: 36, background: "var(--olive-deep, #2d3a2a)" }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="#fbf8f1"><path d="M8 5v14l11-7z" /></svg>
        </span>
        <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--olive-deep)" }}>{watchLabel}</span>
      </button>
    );
  }
  return (
    <div
      className="w-full rounded-lg mb-3 flex items-center justify-center gap-2"
      style={{ height: 84, background: "rgba(45,58,42,0.08)", border: "1px dashed #c9a961" }}
      data-testid={`card-video-placeholder-${card.n}`}
    >
      <span
        className="inline-flex items-center justify-center rounded-full"
        style={{ width: 32, height: 32, background: "var(--olive-deep, #2d3a2a)" }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="#fbf8f1"><path d="M8 5v14l11-7z" /></svg>
      </span>
      <span className="text-xs uppercase tracking-widest" style={{ color: "var(--olive-deep)" }}>{label}</span>
    </div>
  );
}

function Explore() {
  const { t, lang } = useLang();
  const [flipped, setFlipped] = useState({});
  const [videoCard, setVideoCard] = useState(null);
  const closeCard = React.useCallback(() => setVideoCard(null), []);
  return (
    <section id="explore" className="section" style={{ background: "var(--explore-bg)", color: "#fbf8f1" }}>
      <div className="container-x">
        <div className="eyebrow mb-3" style={{ color: "#fbf8f1", opacity: 0.8 }} data-testid="explore-eyebrow">{t.cards.eyebrow}</div>
        <h2 className="h-display mb-3" style={{ fontSize: "clamp(32px, 4vw, 56px)", color: "#fbf8f1" }} data-testid="explore-title">{t.cards.title}</h2>
        <p className="mb-14" style={{ color: "#f0ead8", maxWidth: 600 }} data-testid="explore-sub">{t.cards.sub}</p>

        <div className="flex flex-wrap justify-center gap-6">
          {t.cards.items.map((c, i) => (
            <div
              key={c.n}
              className={`flip-card w-full sm:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] ${flipped[i] ? "is-flipped" : ""}`}
              style={{ minHeight: 400 }}
              onClick={() => setFlipped((p) => ({ ...p, [i]: !p[i] }))}
              data-testid={`flip-card-${i}`}
            >
              <div className="flip-inner" style={{ minHeight: 400 }}>
                <div className="flip-face rounded-2xl p-7 flex flex-col justify-between" style={{ background: "var(--explore-card)", border: "1px solid var(--explore-card-border)" }}>
                  <div className="serif" style={{ fontSize: 36, color: "#f6efd9", opacity: .95 }}>{c.n}</div>
                  <div>
                    <h3 className="serif" style={{ fontSize: 24, color: "#fbf8f1" }}>{c.t}</h3>
                    <div className="mt-4 text-xs uppercase tracking-widest" style={{ color: "#e2dcc6" }}>{t.cards.hint}</div>
                  </div>
                </div>
                <div className="flip-face flip-back rounded-2xl p-6 flex flex-col" style={{ background: "#fbf8f1", border: "1px solid #c9a961" }}>
                  <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--olive-deep)" }}>{c.n} · {c.t}</div>
                  <CardVideoSlot card={c} label={t.cards.videoSoon} watchLabel={t.cards.watch} onWatch={setVideoCard} />
                  <p className="overflow-y-auto pr-1" style={{ fontSize: 13.5, lineHeight: 1.6, color: "#1a1a1a", whiteSpace: "pre-line", flex: 1 }}>{c.d}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Card video popup */}
      {videoCard && (
        <VideoModal
          testId="card-video-modal"
          title={`${videoCard.n} · ${videoCard.t}`}
          src={videoCard.video}
          webm={videoCard.videoWebm}
          captions={videoCard.captions}
          lang={lang}
          labels={{ close: t.about.close, ccLabel: t.about.ccLabel, ccOff: t.about.ccOff, tapSound: t.about.tapSound }}
          onClose={closeCard}
        />
      )}
    </section>
  );
}

/* ---------- Featured Testimonials (WhatsApp style) ---------- */
function ChatBubble({ item, lang }) {
  const dark = item.theme === "dark";
  const wall = dark
    ? { background: "#0b141a", backgroundImage: "radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)", backgroundSize: "18px 18px" }
    : { background: "#e4ddd4", backgroundImage: "radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)", backgroundSize: "18px 18px" };
  const bubble = dark ? { background: "#202c33", color: "#e9edef" } : { background: "#ffffff", color: "#111b21" };
  const timeColor = dark ? "rgba(233,237,239,0.5)" : "rgba(17,27,33,0.45)";
  return (
    <div className="rounded-2xl overflow-hidden flex flex-col" style={{ ...wall, border: "1px solid var(--line)", minHeight: 190 }} data-testid={`featured-${item.id}`}>
      <div className="p-4 md:p-5 flex-1 flex items-end">
        <div style={{ ...bubble, borderRadius: "4px 16px 16px 16px", padding: "10px 12px 7px", maxWidth: "95%", boxShadow: "0 1px 1.5px rgba(0,0,0,0.14)" }}>
          <p style={{ fontSize: 14.5, lineHeight: 1.5, whiteSpace: "pre-line", margin: 0 }}>{item.ro}</p>
          {lang === "en" && (
            <p style={{ fontSize: 12.5, lineHeight: 1.45, whiteSpace: "pre-line", marginTop: 8, opacity: 0.6, fontStyle: "italic" }}>{item.en}</p>
          )}
          {item.time && <div style={{ textAlign: "right", fontSize: 10.5, color: timeColor, marginTop: 5 }}>{item.time}</div>}
        </div>
      </div>
    </div>
  );
}

function FeaturedVideoCard({ video, onOpen, label, name, watch }) {
  return (
    <button onClick={onOpen} data-testid={`featured-video-card-${video.id}`} className="rounded-2xl overflow-hidden relative text-left w-full" style={{ minHeight: 190 }}>
      <img src={video.poster} alt={label} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.72) 100%)" }} />
      <div className="relative flex flex-col justify-between p-5" style={{ minHeight: 190 }}>
        <div className="text-[10px] uppercase tracking-widest" style={{ color: "#e2dcc6" }}>{label}</div>
        <div>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: "#c9a961", boxShadow: "0 12px 30px -12px rgba(201,169,97,0.9)" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#0e0e0e"><path d="M8 5v14l11-7z" /></svg>
          </div>
          <div className="serif" style={{ fontSize: 19, color: "#fff", lineHeight: 1.2 }}>{name}</div>
          <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.82)" }}>{watch}</div>
        </div>
      </div>
    </button>
  );
}

function FeaturedTestimonials() {
  const { t, lang } = useLang();
  const [activeVideo, setActiveVideo] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const f = t.reviews.featured;

  const items = [
    ...FEATURED_VIDEOS.map((v) => ({ kind: "video", key: v.id, video: v })),
    ...FEATURED_TESTIMONIALS.map((b) => ({ kind: "bubble", key: b.id, bubble: b })),
  ];
  const COLLAPSED = 6; // ~2 rows on desktop (3 cols)
  const hasMore = items.length > COLLAPSED;
  const visible = expanded ? items : items.slice(0, COLLAPSED);

  return (
    <div className="mb-16" data-testid="featured-testimonials">
      <div className="eyebrow mb-3" data-testid="featured-eyebrow">{f.eyebrow}</div>
      <h3 className="serif mb-3" style={{ fontSize: "clamp(26px, 3vw, 40px)", lineHeight: 1.1 }} data-testid="featured-title">{f.title}</h3>
      <p className="mb-10" style={{ color: "var(--ink-soft)", maxWidth: 560 }}>{f.sub}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((it) =>
          it.kind === "video" ? (
            <FeaturedVideoCard key={it.key} video={it.video} onOpen={() => setActiveVideo(it.video)} label={f.videoLabel} name={f.videoName} watch={f.watch} />
          ) : (
            <ChatBubble key={it.key} item={it.bubble} lang={lang} />
          )
        )}
      </div>
      {hasMore && (
        <div className="flex justify-center mt-8">
          <button onClick={() => setExpanded((v) => !v)} data-testid="featured-toggle" className="btn-pill text-xs uppercase tracking-widest inline-flex items-center gap-2" style={{ padding: "11px 24px", border: "1px solid #d3c8aa", background: "transparent", color: "#1a1a1a" }}>
            {expanded ? f.less : f.more}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}><path d="M6 9l6 6 6-6" /></svg>
          </button>
        </div>
      )}
      {activeVideo && (
        <VideoModal
          testId="featured-video-modal"
          title={f.videoLabel}
          src={activeVideo.src}
          captions={activeVideo.captions}
          lang={lang}
          labels={{ close: t.about.close, ccLabel: t.about.ccLabel, ccOff: t.about.ccOff, tapSound: t.about.tapSound }}
          onClose={() => setActiveVideo(null)}
        />
      )}
    </div>
  );
}

/* ---------- Reviews ---------- */
function Reviews() {
  const { t, lang } = useLang();
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("all");
  const [mode, setMode] = useState("text"); // text | video | voice
  const [form, setForm] = useState({ age: "", profession: "", content: "", full_name: "", email: "", signature: "" });
  const [consent, setConsent] = useState({ publish: false, data: false });
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews?approved_only=true`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  // lock body scroll when modal open
  useEffect(() => {
    if (showConsent) {
      document.body.style.overflow = "hidden";
      const onKey = (e) => { if (e.key === "Escape") setShowConsent(false); };
      window.addEventListener("keydown", onKey);
      return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
    }
  }, [showConsent]);

  const filtered = useMemo(() => {
    if (filter === "all") return reviews;
    return reviews.filter((r) => r.review_type === filter);
  }, [filter, reviews]);

  const counts = useMemo(() => ({
    all: reviews.length,
    text: reviews.filter((r) => r.review_type === "text").length,
    video: reviews.filter((r) => r.review_type === "video").length,
    voice: reviews.filter((r) => r.review_type === "voice").length,
  }), [reviews]);

  const signatureMatches = form.signature.trim().toLowerCase() === form.full_name.trim().toLowerCase() && form.full_name.trim().length > 1;
  const allConsented = consent.publish && consent.data && signatureMatches;

  // Step 1: validate form, then open consent modal
  const onSubmitClick = (e) => {
    e.preventDefault();
    setMsg("");
    if (mode !== "text") {
      setMsg(mode === "video" ? t.reviews.videoComing : t.reviews.voiceComing);
      return;
    }
    if (!form.content.trim() || !form.full_name || !form.email) {
      setMsg(lang === "ro" ? "Te rog completează numele, emailul și recenzia." : "Please fill in name, email and your review.");
      return;
    }
    setConsent({ publish: false, data: false });
    setForm((f) => ({ ...f, signature: "" }));
    setShowConsent(true);
  };

  // Step 2: from modal, with consent ticked + signature signed, send to backend
  const confirmAndSubmit = async () => {
    if (!allConsented) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        review_type: "text",
        age: form.age,
        profession: form.profession,
        content: form.content,
        full_name: form.full_name,
        email: form.email,
        signature: form.signature,
        consent_publish: consent.publish,
        consent_data: consent.data,
        consent_locale: lang,
        consent_version: "1.0",
      });
      setMsg(t.reviews.thanks);
      setForm({ age: "", profession: "", content: "", full_name: "", email: "", signature: "" });
      setConsent({ publish: false, data: false });
      setShowConsent(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMsg(detail ? `Error — ${detail}` : "Error — please try again.");
    } finally { setSubmitting(false); }
  };

  const dateLabel = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === "ro" ? "ro-RO" : "en-US", { month: "long", day: "numeric", year: "numeric" });
    } catch { return ""; }
  };

  return (
    <section id="reviews" className="section">
      <div className="container-x">
        <div className="eyebrow mb-3" data-testid="reviews-eyebrow">{t.reviews.eyebrow}</div>
        <h2 className="h-display mb-3" style={{ fontSize: "clamp(32px, 4vw, 56px)" }} data-testid="reviews-title">{t.reviews.title}</h2>
        <p className="mb-10" style={{ color: "var(--ink-soft)", maxWidth: 600 }}>{t.reviews.sub}</p>

        <FeaturedTestimonials />

        {reviews.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8">
          {["all", "text", "video", "voice"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`reviews-filter-${k}`}
              className="px-4 py-2 rounded-full text-xs uppercase tracking-widest transition"
              style={{
                background: filter === k ? "#0e0e0e" : "transparent",
                color: filter === k ? "#fbf8f1" : "#1a1a1a",
                border: "1px solid " + (filter === k ? "#0e0e0e" : "#d3c8aa"),
              }}
            >
              {t.reviews.filters[k]}{counts[k] !== undefined ? ` ${counts[k]}` : ""}
            </button>
          ))}
        </div>
        )}

        {reviews.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {filtered.map((r) => (
            <div key={r.id} className="rounded-2xl p-7" style={{ background: "#f7f1e1", border: "1px solid var(--line)" }} data-testid={`review-${r.id}`}>
              <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--olive-deep)" }}>
                {t.reviews.filters[r.review_type] || r.review_type} · {dateLabel(r.created_at)}
              </div>
              <p className="serif italic" style={{ fontSize: 20, lineHeight: 1.5, color: "var(--ink)" }}>“{r.content}”</p>
              <div className="mt-4 text-sm" style={{ color: "var(--ink-soft)" }}>— {r.profession || "Client"}{r.age ? `, ${r.age}` : ""}</div>
            </div>
          ))}
        </div>
        )}

        {/* Submit form */}
        <div className="rounded-3xl p-8 md:p-12" style={{ background: "#fbf8f1", border: "1px solid var(--line)" }}>
          <h3 className="serif mb-2" style={{ fontSize: 32 }} data-testid="reviews-share-title">{t.reviews.share}</h3>
          <p className="mb-8" style={{ color: "var(--ink-soft)" }}>{t.reviews.shareSub}</p>

          <div className="flex gap-2 mb-8">
            {[["text", t.reviews.types.write], ["video", t.reviews.types.video], ["voice", t.reviews.types.voice]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => { setMode(k); setMsg(""); }}
                data-testid={`reviews-mode-${k}`}
                className="px-5 py-2 rounded-full text-sm transition"
                style={{
                  background: mode === k ? "#0e0e0e" : "transparent",
                  color: mode === k ? "#fbf8f1" : "#1a1a1a",
                  border: "1px solid " + (mode === k ? "#0e0e0e" : "#d3c8aa"),
                }}
              >
                {l}
              </button>
            ))}
          </div>

          <form onSubmit={onSubmitClick} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="label-soft">{t.reviews.labels.fullName}</label>
              <input className="input-field" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder={t.reviews.placeholders.fullName} data-testid="review-full-name" />
            </div>
            <div>
              <label className="label-soft">{t.reviews.labels.email}</label>
              <input className="input-field" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.reviews.placeholders.email} data-testid="review-email" />
            </div>
            <div>
              <label className="label-soft">{t.reviews.placeholders.profession}</label>
              <input className="input-field" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} data-testid="review-profession" />
            </div>
            <div>
              <label className="label-soft">{t.reviews.placeholders.age}</label>
              <input className="input-field" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="review-age" />
            </div>
            <div className="md:col-span-2 hidden md:block" />
            <div className="md:col-span-2">
              <label className="label-soft">{t.reviews.labels.content}</label>
              <textarea className="input-field" rows={4} required={mode === "text"} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="review-content" disabled={mode !== "text"} />
            </div>

            <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm flex-1" style={{ color: msg.startsWith("Error") ? "#a94442" : "var(--olive-deep)" }} data-testid="review-msg">{msg}</div>
              <button type="submit" className="btn-pill btn-dark" disabled={submitting} data-testid="review-submit">
                {submitting ? "..." : t.reviews.submit}
              </button>
            </div>
          </form>

          {/* GDPR Consent modal — opens only when user clicks submit */}
          {showConsent && (
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center p-4"
              style={{ background: "rgba(10,10,8,0.7)", backdropFilter: "blur(6px)" }}
              role="dialog"
              aria-modal="true"
              data-testid="gdpr-modal"
              onClick={(e) => { if (e.target === e.currentTarget && !submitting) setShowConsent(false); }}
            >
              <div
                className="rounded-2xl shadow-2xl"
                style={{ background: "#fbf8f1", maxWidth: 640, width: "100%", maxHeight: "90vh", overflowY: "auto", border: "1px solid #e7e0cf" }}
              >
                <div className="p-7 md:p-9">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--gold)", color: "#0e0e0e" }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z" /><path d="M9 12l2 2 4-4" /></svg>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-widest" style={{ color: "var(--olive-deep)" }}>{t.reviews.consent.heading}</div>
                        <div className="serif" style={{ fontSize: 22 }}>{lang === "ro" ? "Citește și semnează" : "Read & sign"}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => !submitting && setShowConsent(false)}
                      aria-label="Close"
                      data-testid="gdpr-modal-close"
                      className="text-sm w-9 h-9 rounded-full hover:bg-[#f4ecd8] transition"
                      style={{ color: "var(--ink-muted)" }}
                    >✕</button>
                  </div>

                  <p className="text-sm mb-5" style={{ color: "var(--ink-soft)", lineHeight: 1.65 }}>{t.reviews.consent.intro}</p>

                  <label className="flex items-start gap-3 mb-4 cursor-pointer" data-testid="consent-publish-label">
                    <input
                      type="checkbox"
                      checked={consent.publish}
                      onChange={(e) => setConsent((c) => ({ ...c, publish: e.target.checked }))}
                      className="mt-1 flex-shrink-0"
                      style={{ width: 18, height: 18, accentColor: "#5a624a" }}
                      data-testid="consent-publish"
                    />
                    <span className="text-sm" style={{ color: "var(--ink)", lineHeight: 1.55 }}>{t.reviews.consent.publishLabel}</span>
                  </label>

                  <label className="flex items-start gap-3 mb-4 cursor-pointer" data-testid="consent-data-label">
                    <input
                      type="checkbox"
                      checked={consent.data}
                      onChange={(e) => setConsent((c) => ({ ...c, data: e.target.checked }))}
                      className="mt-1 flex-shrink-0"
                      style={{ width: 18, height: 18, accentColor: "#5a624a" }}
                      data-testid="consent-data"
                    />
                    <span className="text-sm" style={{ color: "var(--ink)", lineHeight: 1.55 }}>{t.reviews.consent.dataLabel}</span>
                  </label>

                  <p className="text-xs mb-5" style={{ color: "var(--ink-muted)", lineHeight: 1.55 }}>{t.reviews.consent.withdraw}</p>

                  <div className="pt-4 border-t" style={{ borderColor: "#e7e0cf" }}>
                    <label className="label-soft">{t.reviews.labels.signature}</label>
                    <input
                      className="input-field"
                      value={form.signature}
                      onChange={(e) => setForm({ ...form, signature: e.target.value })}
                      placeholder={t.reviews.placeholders.signature}
                      data-testid="review-signature"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 22 }}
                    />
                    <div className="mt-2 text-[11px]" style={{ color: signatureMatches ? "#3c4a30" : "var(--ink-muted)" }} data-testid="signature-status">
                      {form.signature.trim() === ""
                        ? (lang === "ro" ? `Pentru a semna, scrie exact: "${form.full_name}"` : `To sign, type exactly: "${form.full_name}"`)
                        : signatureMatches
                          ? (lang === "ro" ? "✓ Semnătură validă" : "✓ Signature valid")
                          : (lang === "ro" ? "Semnătura trebuie să fie identică cu numele complet." : "Signature must match your full name exactly.")
                      }
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => !submitting && setShowConsent(false)}
                      disabled={submitting}
                      className="btn-pill btn-outline"
                      style={{ padding: "12px 22px" }}
                      data-testid="gdpr-cancel"
                    >
                      {lang === "ro" ? "Anulează" : "Cancel"}
                    </button>
                    <button
                      type="button"
                      onClick={confirmAndSubmit}
                      disabled={!allConsented || submitting}
                      className="btn-pill btn-dark"
                      style={{ opacity: (!allConsented || submitting) ? 0.5 : 1 }}
                      data-testid="gdpr-confirm"
                    >
                      {submitting ? "..." : t.reviews.submit}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------- Booking ---------- */
function Booking() {
  const { t, lang } = useLang();
  const options = [t.book.miniSession, ...t.services.list.map((s) => s.t)];
  const [choice, setChoice] = useState(options[0]);
  const isMini = choice === t.book.miniSession;

  return (
    <section id="book" className="section" style={{ background: "linear-gradient(180deg,#fbf8f1 0%,#f4ecd8 100%)" }}>
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left — invitation */}
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3" data-testid="book-eyebrow">{t.book.eyebrow}</div>
            <h2 className="h-display mb-5" style={{ fontSize: "clamp(34px, 4.5vw, 56px)", lineHeight: 1.04 }} data-testid="book-title">{t.book.title}</h2>
            <p className="mb-8" style={{ color: "var(--ink-soft)", fontSize: 17, lineHeight: 1.65, maxWidth: 460 }} data-testid="book-sub">{t.book.sub}</p>
            <div className="inline-flex items-start gap-3 rounded-2xl px-5 py-4" style={{ background: "rgba(201,169,97,0.14)", border: "1px solid #c9a961", maxWidth: 460 }} data-testid="book-mini-note">
              <span className="mt-0.5 flex-shrink-0 rounded-full flex items-center justify-center" style={{ width: 26, height: 26, background: "#c9a961", color: "#0e0e0e", fontSize: 11, fontWeight: 600 }}>25</span>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: "var(--ink)" }}>{t.book.miniNote}</p>
            </div>
          </div>

          {/* Right — service picker + CTA */}
          <div className="lg:col-span-7">
            <div className="rounded-[28px] p-8 md:p-10" style={{ background: "#fbf8f1", border: "1px solid var(--line)", boxShadow: "0 30px 70px -40px rgba(60,48,20,0.4)" }} data-testid="book-card">
              <div className="label-soft mb-1" data-testid="book-choose-label">{t.book.chooseLabel}</div>
              <p className="mb-6" style={{ color: "var(--ink-muted)", fontSize: 13.5, lineHeight: 1.55 }} data-testid="book-choose-hint">{t.book.chooseHint}</p>

              <div className="flex flex-wrap gap-2.5 mb-9">
                {options.map((opt, i) => {
                  const selected = choice === opt;
                  const mini = opt === t.book.miniSession;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setChoice(opt)}
                      data-testid={`book-opt-${i}`}
                      className="rounded-full transition"
                      style={{
                        fontSize: 13.5,
                        padding: "9px 16px",
                        fontWeight: selected ? 600 : 400,
                        background: selected ? "#0e0e0e" : (mini ? "rgba(201,169,97,0.16)" : "transparent"),
                        color: selected ? "#fbf8f1" : "#1a1a1a",
                        border: "1px solid " + (selected ? "#0e0e0e" : (mini ? "#c9a961" : "#d3c8aa")),
                      }}
                    >
                      {mini ? `★ ${opt}` : opt}
                    </button>
                  );
                })}
              </div>

              <div className="relative inline-block">
                <button
                  type="button"
                  className="btn-pill btn-dark"
                  onClick={() => openBooking(lang, isMini ? "" : choice)}
                  data-testid="book-cta"
                >
                  {isMini ? t.book.cta : `${t.book.bookThis || "Book"}: ${choice}`}
                </button>
                {isMini && (
                  <span
                    className="absolute -top-3 -right-3 sm:-right-5 rounded-full whitespace-nowrap hero-badge-pop"
                    style={{ background: "#c9a961", color: "#0e0e0e", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, padding: "5px 10px", boxShadow: "0 8px 20px -8px rgba(201,169,97,0.8)", transform: "rotate(-4deg)" }}
                    data-testid="book-mini-badge"
                  >
                    {t.hero.badge}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  const { t } = useLang();
  return (
    <section id="faq" className="section">
      <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="eyebrow mb-3" data-testid="faq-eyebrow">{t.faq.eyebrow}</div>
          <h2 className="h-display" style={{ fontSize: "clamp(32px, 4vw, 48px)" }} data-testid="faq-title">{t.faq.title}</h2>
        </div>
        <div className="md:col-span-8">
          {t.faq.items.map((it, i) => (
            <details key={i} className="border-b py-5" style={{ borderColor: "var(--line)" }} data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer">
                <span className="serif" style={{ fontSize: 22 }}>{it.q}</span>
                <span style={{ fontSize: 24, color: "var(--olive-deep)" }}>+</span>
              </summary>
              <p className="mt-3" style={{ color: "var(--ink-soft)", lineHeight: 1.7, maxWidth: 680 }}>{it.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Footer CTA strip (keeps existing dark CTA) ---------- */
const SERENITY_IMG = "https://customer-assets.emergentagent.com/job_calm-scheduler-2/artifacts/vz5h6q2k_WhatsApp%20Image%202026-05-08%20at%2008.00.06.jpeg";
function Footer() {
  const { t, lang } = useLang();
  return (
    <section id="footer" className="relative grain" style={{ background: "#0e0e0e", color: "#fbf8f1", padding: "100px 0 80px" }}>
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 text-center md:text-left">
            <h2 className="h-display mb-4" style={{ fontSize: "clamp(36px, 5vw, 64px)" }} data-testid="footer-title">{t.footer.title}</h2>
            <p className="mb-10" style={{ color: "#bdb7a3", fontSize: 18 }} data-testid="footer-sub">{t.footer.sub}</p>
            <div className="relative inline-block">
              <button
                className="btn-pill"
                style={{ background: "#fbf8f1", color: "#0e0e0e" }}
                onClick={() => openBooking(lang)}
                data-testid="footer-cta"
              >
                {t.footer.cta}
              </button>
              <span
                className="absolute -top-3 -right-3 sm:-right-5 rounded-full whitespace-nowrap hero-badge-pop"
                style={{ background: "#c9a961", color: "#0e0e0e", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, padding: "5px 10px", boxShadow: "0 8px 20px -8px rgba(201,169,97,0.8)", transform: "rotate(-4deg)" }}
                data-testid="footer-mini-badge"
              >
                {t.hero.badge}
              </span>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-[24px] overflow-hidden" style={{ aspectRatio: "1/1", boxShadow: "0 30px 80px -30px rgba(0,0,0,0.6)" }}>
              <img
                src={SERENITY_IMG}
                alt="Stones balanced beside clasped hands at sunset"
                className="w-full h-full object-cover"
                data-testid="footer-image"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home({ forceLang }) {
  const { lang, setLang } = useLang();
  const seoLang = forceLang || lang;

  useEffect(() => {
    if (forceLang) setLang(forceLang);
  }, [forceLang, setLang]);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, []);

  const seo = seoLang === "ro"
    ? {
        title: "Psiholog online și consiliere psihologică | Silvana Capris",
        description: "Psiholog online și consiliere psihologică în română și engleză, cu psihologul clinician Silvana Capris. Sprijin pentru anxietate, stres, familie și boală — pentru adulți, tineri și familii. Prima mini-ședință de 25 de minute este gratuită.",
        canonical: "https://antrenordecreier.ro/",
      }
    : {
        title: "Romanian & English-Speaking Psychologist Online | Silvana Capris",
        description: "Online psychologist and counselling in English and Romanian, with clinical psychologist Silvana Capris. Support for anxiety, stress, family and illness — for adults, youth and families. Your first 25-minute mini-session is free.",
        canonical: "https://antrenordecreier.ro/en",
      };

  return (
    <div className="App" data-testid="home-page">
      <Seo
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        lang={seoLang}
        altRo="https://antrenordecreier.ro/"
        altEn="https://antrenordecreier.ro/en"
      />
      <Nav langUrls={{ ro: "/", en: "/en" }} />
      <Hero />
      <Quote />
      <About />
      <Services />
      <Explore />
      <Reviews />
      <Booking />
      <Conversations />
      <FAQ />
      <Footer />
      <LegalFooter />
      <CookieBanner />
      <ExploreColorPicker />
    </div>
  );
}
